import React, { useCallback, useEffect, useRef, useState } from "react";
import { CloseButton, Col, Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

const SignatureInput = () => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [canvasWidth, setCanvasWidth] = useState(450);
    const {
        getValues,
        register,
        setValue,
        formState: { errors },
    } = useFormContext();

    const startDrawing = e => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
        setIsDrawing(true);
    };

    const draw = e => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        const signatureDataUrl = canvas.toDataURL();
        setValue("signature", signatureDataUrl);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setValue("signature", "");
    };

    const adjustCanvasSize = () => {
        if (canvasRef.current) {
            const parentWidth =
                canvasRef.current.parentElement.offsetWidth - 24;
            setCanvasWidth(parentWidth);
        }
    };

    const drawExistingSignature = useCallback(() => {
        const existingSignature = getValues("signature");
        if (existingSignature && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            const image = new Image();
            image.src = existingSignature;
            image.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            };
        }
    }, [getValues]);

    useEffect(() => {
        adjustCanvasSize();
        drawExistingSignature();
        window.addEventListener("resize", adjustCanvasSize);
        return () => {
            window.removeEventListener("resize", adjustCanvasSize);
        };
    }, [drawExistingSignature]);

    return (
        <Form.Group as={Col} md={6} className="mb-4">
            <Form.Label>
                Signature <span className="required-label">*</span>
            </Form.Label>
            <Form.Text
                className="ms-md-1 d-block d-md-inline"
                id="signature_help_block"
                muted
            >
                Use your mouse or touchpad to draw your signature below.
            </Form.Text>
            <div className="position-relative">
                <CloseButton
                    onClick={clearCanvas}
                    aria-label="Clear"
                    className="position-absolute top-0 end-0 me-4 mt-1"
                />

                <canvas
                    ref={canvasRef}
                    width={canvasWidth}
                    height={150}
                    className="d-block border rounded border-dark"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                />
            </div>

            <Form.Control
                type="hidden"
                {...register("signature", {
                    required: "Signature is required",
                })}
            />

            <Form.Control.Feedback type="invalid" className="d-block">
                {errors["signature"]?.message}
            </Form.Control.Feedback>
        </Form.Group>
    );
};

export default SignatureInput;
